import { Link } from "react-router-dom";
import { WidgetFrame } from "@/dashboard/WidgetFrame";
import { useMyRecentCommunications } from "../hooks/useCommunications";
import { ChannelIcon } from "./ChannelIcon";
import { relativeTime } from "@/components/ux/relativeTime";

interface Props {
  limit?: number;
}

const SNIPPET_LEN = 60;

/**
 * Comm Log Standard §9.5 — top 5 entries from
 * <c>GET /api/communications/my-recent</c>. Sits in the dashboard's personal
 * band adjacent to <c>MyOpenTasksWidget</c>, inside the shared WidgetFrame
 * chrome; same shape and visual weight.
 */
export function MyRecentCommunicationsWidget({ limit = 5 }: Props) {
  const q = useMyRecentCommunications(limit);

  return (
    <WidgetFrame
      title="My recent communications"
      to="/communications"
      isLoading={q.isLoading}
      isError={q.isError}
      isEmpty={q.data?.length === 0}
      emptyText="No recent communications. Log a call from any record to get started."
    >
      {q.data && q.data.length > 0 && (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {q.data.map((entry) => {
            const counterparty =
              entry.participants[0]?.displayName ??
              entry.externalPartyName ??
              entry.externalPhoneNumber ??
              "Unknown";
            const snippet = (entry.subject ?? entry.body).slice(0, SNIPPET_LEN);
            return (
              <li key={entry.id}>
                <Link
                  to={`/communications/${entry.id}`}
                  className="flex items-center justify-between gap-space-2 rounded-md p-space-2 -mx-space-2 hover:bg-white-warm"
                >
                  <div className="flex items-start gap-space-2 min-w-0">
                    <ChannelIcon channel={entry.channel} size={16} />
                    <span className="flex flex-col min-w-0">
                      <span className="text-small text-onyx truncate font-semibold">
                        {counterparty}
                      </span>
                      <span className="text-label text-gray-mid truncate">
                        {snippet}
                      </span>
                    </span>
                  </div>
                  <span className="text-label text-gray-mid shrink-0 tabular-nums">
                    {relativeTime(entry.occurredAt)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetFrame>
  );
}
